import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';

import Form from '../../../../../antd/form/Form/Form.jsx';
import FormTextField from '../../../../../antd/form/FormTextField/FormTextField.jsx';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import FormNumberField from '../../../../../antd/form/FormNumberField/FormNumberField.jsx';
import { number, required } from '../../../../../antd/form/FormValidation';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';

const IngProjectsActiveStudentsCreateForm = ({ onSubmit, form, students, projects, isLoading, defaultYear }) => {
  const { t } = useTranslation();
  const [projectStudyProgramsOptions, setProjectStudyProgramsOptions] = useState([]);

  // Filter only active projects
  const activeProjects = projects?.filter((project) => project.active) || [];

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  // When a project is selected, update the study program field
  const handleProjectChange = (projectId) => {
    if (projectId) {
      // Find the project and get its study programs
      const project = activeProjects.find((p) => p.id === projectId);
      if (project) {
        // Reset the selected study program
        form.setFieldsValue({
          study_program_id: null,
        });

        // If project has study_programs array, create options from it
        if (project.study_programs && Array.isArray(project.study_programs)) {
          const studyProgramsOptions = project.study_programs.map((sp) => ({
            value: sp.id,
            label: sp.name,
          }));
          setProjectStudyProgramsOptions(studyProgramsOptions);
        } else {
          // If no study programs, reset options
          setProjectStudyProgramsOptions([]);
        }
      }
    } else {
      // Reset both fields if project is cleared
      form.setFieldsValue({
        study_program_id: null,
      });
      setProjectStudyProgramsOptions([]);
    }
  };

  const studentOptions = useMemo(
    () =>
      students?.map((student) => ({
        value: student.id,
        label: `${student.full_name} (${student.study_group})`,
      })) || [],
    [students],
  );

  const projectOptions = useMemo(
    () =>
      activeProjects?.map((project) => ({
        value: project.id,
        label: project.name + (project.study_program_name ? ` (${project.study_program_name})` : ''),
      })) || [],
    [activeProjects],
  );

  return (
    <Form form={form} onFinish={handleSubmit} initialValues={{ year: defaultYear }}>
      <FormSelect
        label={t('Študent')}
        name="student_id"
        placeholder={t('Vyberte študenta')}
        options={studentOptions}
        extra={t('V zozname sa zobrazujú iba študenti, ktorí nemajú pridelený projekt.')}
        rules={[required(t)]}
      />

      <FormSelect
        label={t('Projekt')}
        name="ip_project_id"
        placeholder={t('Vyberte projekt')}
        options={projectOptions}
        onChange={handleProjectChange}
        rules={[required(t)]}
      />

      <FormSelect
        label={t('Študijný program')}
        name="study_program_id"
        placeholder={t('Vyberte študijný program')}
        options={projectStudyProgramsOptions}
        disabled={!form.getFieldValue('ip_project_id') || projectStudyProgramsOptions.length === 0}
        rules={[required(t)]}
      />

      <FormNumberField
        label={t('Rok nástupu')}
        name="year"
        placeholder={t('Rok nástupu')}
        min={2000}
        max={2100}
        precision={0}
        style={{ width: '100%' }}
        rules={[required(t), number(t, 2000, 2100)]}
      />

      <FormButtonsContainer>
        <FormButton submitOnClick={true} submitKey={'ip-active-students-create'} loading={isLoading}>
          {t('Uložiť')}
        </FormButton>
      </FormButtonsContainer>
    </Form>
  );
};

export default IngProjectsActiveStudentsCreateForm;
